import type { ListResponse, ProfileResponse } from '@shared/schemas';

import type { SourceListOrigin } from '../infrastructure/list-import-query';
import { applyExclusion } from './list-import';

/** Paginated preview of the imported profiles, filled by the fetch saga. */
export interface ImportPreviewState {
  rows: ProfileResponse[];
  total: number;
  loading: boolean;
  /** 0-indexed page (the gateway converts to the 1-indexed server page). */
  page: number;
  pageSize: number;
}

/** Default preview page size; matches the old `useProfilesByIds(..., 25)`. */
export const IMPORT_PREVIEW_PAGE_SIZE = 25;

export const initialImportPreviewState: ImportPreviewState = {
  rows: [],
  total: 0,
  loading: false,
  page: 0,
  pageSize: IMPORT_PREVIEW_PAGE_SIZE,
};

/**
 * State of the "Import from existing list" step, shared by both wizards.
 *
 * Nested under `listImport` in each slice on purpose: both slices already own a
 * top-level `excludeDuplicates` / `duplicateCount` / `duplicateLoading` for the
 * *profile-filter* duplicate concern, which is a different thing. Flattening
 * these in would collide silently.
 */
export interface ListImportState {
  origin: SourceListOrigin;
  /** `''` = every licence (the dropdown's empty selection). */
  sourceLicenceId: string;
  sourceLists: ListResponse[];
  sourceListsLoading: boolean;
  importing: boolean;
  /** Ids as held by the source list, before duplicate exclusion. */
  rawImportedIds: string[];
  duplicateIds: string[];
  excludeDuplicates: boolean;
  /** Paginated preview of the selected profiles (fed by the fetch saga). */
  preview: ImportPreviewState;
}

export const initialListImportState: ListImportState = {
  origin: 'all',
  sourceLicenceId: '',
  sourceLists: [],
  sourceListsLoading: false,
  importing: false,
  rawImportedIds: [],
  duplicateIds: [],
  excludeDuplicates: true,
  preview: initialImportPreviewState,
};

/**
 * The slice shape these transitions act on. Both wizards satisfy it
 * structurally, so the step behaves identically in Add and Edit without either
 * slice importing the other.
 */
export interface ListImportTarget {
  listImport: ListImportState;
  importedProfileIds: string[];
  importedListName: string;
}

/**
 * One atomic transition for a completed import: store the raw data *and* derive
 * the selection through `applyExclusion`, so the two can never drift apart.
 */
export function applyImportSucceeded(
  state: ListImportTarget,
  payload: { rawIds: string[]; duplicateIds: string[]; listName: string },
): void {
  const { rawIds, duplicateIds, listName } = payload;
  state.listImport.importing = false;
  state.listImport.rawImportedIds = rawIds;
  state.listImport.duplicateIds = duplicateIds;
  state.importedProfileIds = applyExclusion(
    rawIds,
    duplicateIds,
    state.listImport.excludeDuplicates,
  );
  state.importedListName = listName;
  // A fresh import restarts the preview from the first page; the saga refetches.
  state.listImport.preview.page = 0;
}

/** Flip the exclusion toggle and re-derive the selection from the stored ids. */
export function applyImportExclusionToggled(state: ListImportTarget): void {
  const next = !state.listImport.excludeDuplicates;
  state.listImport.excludeDuplicates = next;
  if (state.listImport.rawImportedIds.length === 0) return;
  state.importedProfileIds = applyExclusion(
    state.listImport.rawImportedIds,
    state.listImport.duplicateIds,
    next,
  );
}

/** `ids` with `fromId` swapped for `toId` in place, kept once if `toId` is already there. */
function replaceId(ids: string[], fromId: string, toId: string): string[] {
  if (!ids.includes(fromId)) return ids;
  if (ids.includes(toId)) return ids.filter((id) => id !== fromId);
  return ids.map((id) => (id === fromId ? toId : id));
}

/**
 * A profile of the import was merged into another from the drawer (#746): its
 * id is gone, so the selection holds the kept profile's id instead, once, and
 * is re-derived through `applyExclusion` like every other transition. An id
 * the import does not hold leaves the state as it was.
 */
export function applyImportedProfileReplaced(
  state: ListImportTarget,
  payload: { fromId: string; toId: string },
): void {
  const { fromId, toId } = payload;
  const { rawImportedIds, duplicateIds } = state.listImport;
  if (fromId === toId) return;
  if (!rawImportedIds.includes(fromId) && !duplicateIds.includes(fromId)) return;
  state.listImport.rawImportedIds = replaceId(rawImportedIds, fromId, toId);
  state.listImport.duplicateIds = replaceId(duplicateIds, fromId, toId);
  state.importedProfileIds = applyExclusion(
    state.listImport.rawImportedIds,
    state.listImport.duplicateIds,
    state.listImport.excludeDuplicates,
  );
}

/**
 * Clear the import. Deliberately leaves `excludeDuplicates` alone; the previous
 * step's "Clear import" button never reset the user's toggle either.
 */
export function applyImportCleared(state: ListImportTarget): void {
  state.listImport.rawImportedIds = [];
  state.listImport.duplicateIds = [];
  state.importedProfileIds = [];
  state.importedListName = '';
  state.listImport.preview = initialImportPreviewState;
}

// ── Import-preview transitions (paginated preview table of the selection) ──

export function applyImportPreviewLoading(state: ListImportState): void {
  state.preview.loading = true;
}

export function applyImportPreviewLoaded(
  state: ListImportState,
  payload: { rows: ProfileResponse[]; total: number },
): void {
  state.preview.rows = payload.rows;
  state.preview.total = payload.total;
  state.preview.loading = false;
}

export function applyImportPreviewPageChanged(state: ListImportState, page: number): void {
  state.preview.page = page;
}

/** Changing the page size resets to the first page (parity with the profile table). */
export function applyImportPreviewPageSizeChanged(state: ListImportState, pageSize: number): void {
  state.preview.pageSize = pageSize;
  state.preview.page = 0;
}
